import { resolveDefaultCriteria } from './defaultCriteria';
import { DEFAULT_TOURISTS, isKid, isInfant } from '../config/tourists';
import { type SearchCriteria, type Selection, type TouristsSelection } from '../model/types';
import { getCountryResorts, findHotels } from '@/shared/api';
import { useTravelataAppConfig } from '@/shared/composables';
import {
  type Country,
  type Hotel,
  type Resort,
  type SearchFormData,
  type SearchCriteriaParams,
  type DateRange,
  type NightsRange,
  type DepartureCity,
} from '@/shared/model';

export async function buildSearchCriteria(
  params: SearchCriteriaParams | null,
  formData: SearchFormData,
): Promise<SearchCriteria> {
  const { cities, countries } = formData;
  let destination: Selection | null = null;
  let dateRange: DateRange | null = null;
  let nights: NightsRange | null = null;
  let tourists: TouristsSelection | null = null;
  const appConfig = useTravelataAppConfig();

  if (params) {
    dateRange = {
      daysFlexible: 0,
      ...params.checkInDateRange,
    };
    nights = {
      ...params.nightRange,
    };
    tourists = {
      rooms: [
        {
          adults: params.touristGroup.adults,
          childrenAges: params.touristGroup.ages,
        },
      ],
    };
  }
  const country = countries.find((c) => c.id === params?.countries[0]) || countries[0];
  const city =
    cities.find((c) => c.id === params?.departureCity.id) ||
    cities.find((c) => c.id === appConfig.defaultDepartureCityId);
  const fromCity: DepartureCity | null = city || null;
  const resorts = await getCountryResorts(country);
  const defaultCriteria = resolveDefaultCriteria(formData.defaultCriteria, country.id, fromCity?.type);

  if (!dateRange) {
    dateRange = defaultCriteria.dateRange || null;
  }
  if (!nights) {
    nights = defaultCriteria.nights || null;
  }
  if (!tourists) {
    tourists = DEFAULT_TOURISTS;
  }

  if (params?.resorts) {
    destination = {
      country: { ...country, resorts },
      resorts: resorts.filter((r) => params.resorts?.includes(r.id)),
      kind: 'resorts',
    };
  } else if (params?.hotels) {
    const hotels = await findHotels({ country: country.id, hotels: params.hotels });
    destination = {
      kind: 'hotels',
      hotels,
      country: { ...country, resorts },
    };
  } else {
    destination = {
      kind: 'country',
      country: { ...country, resorts },
    };
  }

  return {
    dateRange,
    destination,
    tourists,
    nights,
    fromCity,
  };
}

export function buildSearchCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const countries: Country[] = [];
  const resorts: Resort[] = [];
  const hotels: Hotel[] = [];
  const { destination } = params;

  if (destination?.kind !== 'none' && destination?.country) {
    countries.push(destination?.country);
  }
  if (destination?.kind === 'resorts') {
    resorts.push(...destination.resorts);
  }
  if (destination?.kind === 'hotels') {
    hotels.push(...destination.hotels);
  }

  const room = params.tourists?.rooms[0];

  return {
    checkInDateRange: {
      from: params.dateRange?.from || '',
      to: params.dateRange?.to || '',
    },
    nightRange: {
      from: params.nights?.from || 0,
      to: params.nights?.to || 0,
    },
    departureCity: {
      id: params.fromCity?.id || 0,
      name: params.fromCity?.name || '',
    },
    touristGroup: {
      adults: room?.adults || 0,
      kids: room?.childrenAges?.filter(isKid).length || 0,
      infants: room?.childrenAges?.filter((age) => isInfant(age)).length || 0,
      ages: room?.childrenAges || [],
    },
    countries: countries.map(({ id }) => id),
    resorts: resorts.map(({ id }) => id),
    hotels: hotels.map(({ id }) => id),
  };
}
export function buildStorageCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const storageParams = buildSearchCriteriaParams(params);

  if (!storageParams.resorts?.length) {
    delete storageParams.resorts;
  }
  if (!storageParams.hotels?.length) {
    delete storageParams.hotels;
  }

  const daysFlexible = params.dateRange?.daysFlexible;
  if (daysFlexible) {
    storageParams.checkInDateRange = { ...storageParams.checkInDateRange, daysFlexible };
  }

  return storageParams;
}
